import { NumberArray } from '@wonderlandengine/api';
import { CloudData } from '../data/cloud-data.js';
import { ViewStates } from '../components/app.js';

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

export enum PaintTypes {
	Solid,
	Metallic,
	Solitallic
}

export enum WheelTypes {
	Alloy,
	Wire
}

export enum DoorGraphicTypes {
	Black,
	Clear,
	White
}

export enum LeatherTypes {
	TwoTone,
	PebbleGrain,
	SoftGrain
}

// the back-office option ids a bespoke ('select your own') body colour is recorded as, one per paint type that offers it.
// the id carries the description and price; the colour itself is not part of it, and lives in 'bespokeBodyPaintColour' or
// 'bespokeWingPaintColour'. the same ids serve both regions, so an id alone does not say which colour was chosen - see isSamePaint().
export enum BespokePaintColours {
	Solid = 'BCSF001',
	Metallic = 'BCSF002',
}

export enum PaintColourWaysOptions {
	TwoTone = 'BCSF005',
	SingleColour = 'BCSF009'
}

export enum SeatBackStyles {
	LeatherWrapped = 'SB002',
	TwoToneTextile = 'NONE'
}

export enum SeatPleatingStyles {
	Horizontal = 'T001',
	Quilted = 'T002',
	Box = 'T003'
}

export enum WheelNutOptions {
	Polished = 'WS001',
	Brass = 'WS002',
	None = 'WS003',
}

export enum SpareWheelOptions {
	Spare = 'SPARE',
	None = 'NOSPARE',
}

export enum WoodTypes {
	Standard = 'STANDARD',
	Engineered = 'ENGINEERED'
}

export enum AudioOptions {
	Bluetooth = 'CP004',
	Sennheiser = 'CP010',
}

export enum AccentOptions {
	NoBadge = 'B003',
	BadgeBlack = 'B001',
	BadgeColoured = 'B002',
	AccentPack = 'EA001'
}

export enum TunnelSidesOptions {
	Carpet = 'LO001',
	Leather = 'LO002'
}

export enum EmbroideryOptions {
	None = 'HR004',
	Wings = 'HR001',
	Custom = 'HR003'
}

export enum LuggageRackOptions {
	Silver = 'AM031',
	Black = 'AM033',
	Stowage = 'AM029'
}

export enum TransmissionOptions {
	Manual = 'MS008',
	Auto = 'MS005',
}

export enum SpareWheelCoverOptions {
	WheelCover = 'COV014',
	WheelDisc = 'W016',
}

interface CostBreakdown {
	transmission: number;
	paintwork: number;
	wheels: number;
	interior: number;
	cockpit: number;
	exterior: number;
	accessories: number;
}

interface DebugOptions {
	autoStart: boolean;
	autoStartView: ViewStates;
}

//-----------------------------------------------------------------------------------------
// Constants:
//-----------------------------------------------------------------------------------------

const MODEL_ID = '4';																	// plus four.
const SEND_TO_DEALER_MOBILE_LEAD_ID = '194';											// send to dealer lead ID for mobile.
const SEND_TO_DEALER_DESKTOP_LEAD_ID = '97';                                            // send to dealer lead ID for desktop.
const PDF_DOWNLOAD_MOBILE_LEAD_ID = '198';                                              // pdf download lead ID for mobile.
const PDF_DOWNLOAD_DESKTOP_LEAD_ID = '243';                                             // pdf download lead ID for desktop.

const DEBUG_USE_PDFV2_TEMPLATE = true;													// debug flag to use the new PDF template (v2) instead of the old one (v1).

const PIPING_MATCHING_LEATHER_STYLE_ID = 'P001';										// piping matching leather style ID.
const STITCHING_MATCHING_LEATHER_STYLE_ID = 'S001';										// stitching matching leather style ID.
const EMBROIDERY_OPTION_MATCHING_LEATHER_STYLE_ID = 'HR004';							// headrest embroidery option matching leather style ID.

const DISCONTINUED_LEATHER_STYLE_IDS: readonly string[] = ['LS007'];					// leather styles no longer offered, but still held by saved configurations.

const DASHBOARD_MATCHING_GLOSS_BODY_COLOUR_ID = 'D001';									// dashboard matching gloss body colour ID.
const DASHBOARD_MATCHING_MATTE_BODY_COLOUR_ID = 'D027';									// dashboard matching matte body colour ID.
const SEAT_PLEATING_OPTION_FOR_TWO_TONE_LEATHER = 'T001';								// seat pleating option for two-tone leather.

const HEATED_SEATS_OPTION = 'SF001';													// heated seats option.
const COMFORT_PLUS_OPTION = 'SF004';													// comfort plus option.
const SILVER_SPORTS_EXHAUST_OPTION = 'E009';											// silver sports exhaust option.
const BLACK_SPORTS_EXHAUST_OPTION = 'E010';												// black sports exhaust option.
const CUP_HOLDER_OPTION = 'SF005';														// cup holder option.
const CONNECTIVITY_PACK_OPTION = 'CP012';												// connectivity pack option.
const AIR_CON_OPTION = 'CP001';															// air con option.
const TUNNEL_STORAGE_OPTION = 'SBAG014';												// tunnel storage option.
const SIDESCREEN_STORAGE_OPTION = 'SBAG013';											// tunnel storage option.
const PRE_EQUIPMENT_PACK_OPTION = 'AM032';												// pre-equipment pack aftermarket accessory option.
const BLACK_LUGGAGE_RACK_OPTION = 'AM033';												// black luggage rack option.
const BLACK_GRILLE_MESH_OPTION_ID = 'COWL004';											// black grille mesh option.

const DEFAULT_REGION_CODE = 'gb';														// uk.
const DEFAULT_LANGUAGE_CODE = 'en';														// english.
const DEFAULT_TRANSMISSION_ID = 'MS008';												// manual transmission.
const DEFAULT_PAINT_COLOUR_ID = 'PS002';												// sport blue for body and wings.
const DEFAULT_WHEEL_STYLE_ID = 'W005';													// 15" alloy silver.
const DEFAULT_WHEEL_NUT_OPTION_ID = WheelNutOptions.None as string;						// no wheel nuts.
const DEFAULT_LEATHER_STYLE_ID = 'LP018';												// two-tone pebble (black/grey).
const DEFAULT_TUNNEL_SIDES_STYLE_ID = 'LO001';											// carpet tunnel sides.
const DEFAULT_SEAT_BACK_STYLE_ID = SeatBackStyles.TwoToneTextile as string;				// two-tone textile.
const DEFAULT_LEATHER_PIPING_OPTION_ID = PIPING_MATCHING_LEATHER_STYLE_ID;				// matching leather style.
const DEFAULT_SEAT_STITCHING_OPTION_ID = 'S001';										// matching upholstery style.
const DEFAULT_CARPET_COLOUR_ID = 'CSW001';												// black standard weave.
const DEFAULT_DASHBOARD_OPTION_ID = 'D024';												// matte silver dashboard face.
const DEFAULT_SPARE_WHEEL_OPTION_ID = SpareWheelOptions.None as string;					// no spare wheel.
const DEFAULT_STEERING_WHEEL_OPTION_ID = 'SWC001';										// silver steering wheel.
const DEFAULT_HOOD_STYLE_OPTION_ID = 'H001';											// black hood.
const DEFAULT_WING_BEADING_OPTION_ID = 'WB001';											// black wing beading.
const DEFAULT_SEAT_PLEATING_OPTION_ID = SeatPleatingStyles.Horizontal as string;		// horizontal stitch.
const DEFAULT_HEADREST_EMBROIDERY_OPTION_ID = 'HR004';									// no logo on headrest.
const DEFAULT_CENTRE_CONSOLE_OPTION_ID = 'CW012';										// ash olive centre console.
const DEFAULT_STONE_GUARD_OPTION_ID = 'COWL001';										// polished stone guard.
const DEFAULT_GRILLE_MESH_OPTION_ID = 'COWL003';										// polished grille mesh.
const DEFAULT_SPLITTER_OPTION_ID = 'SD001';												// dark satin painted splitter.
const DEFAULT_ACCENT_OPTION_ID = 'B003';												// no badge.
const DEFAULT_ACCENT_PACK_OPTION_ID = 'NONE';											// no accent pack.
const DEFAULT_DOOR_GRAPHIC_OPTION_ID = 'NONE';											// no door graphic.
const DEFAULT_PILOT_VALUE = '';															// no pilot value.
const DEFAULT_COPILOT_VALUE = '';														// no copilot value.
const DEFAULT_CUSTOM_FLAG_VALUE = '';													// no custom flag value.
const DEFAULT_CALL_SIGNS_FLAG_OPTION_ID = 'NONE';										// no call sign flag.
const DEFAULT_AUDIO_OPTION_ID = 'CP004';												// bluetooth audio.
const DEFAULT_PAINT_PROTECTION_FILM_OPTION = 'NONE';									// no paint protection film.

/**
 * Configuration:
 */
export abstract class Configuration {

	//-----------------------------------------------------------------------------------------
	// Fields:
	//-----------------------------------------------------------------------------------------
	
	private static _costBreakdown: CostBreakdown = { transmission: 0, paintwork: 0, wheels: 0, interior: 0, cockpit: 0, exterior: 0, accessories: 0 };
	private static _totalCost = 0;
	private static _invalidOptionSelected = false;

	//-----------------------------------------------------------------------------------------
	// Properties:
	//-----------------------------------------------------------------------------------------

	static get costBreakdown() {
		return this._costBreakdown;
	}

	static get totalCost() {
		return this._totalCost;
	}

	static get invalidOptionSelected() {
		return this._invalidOptionSelected;
	}

	static get pipingMatchingLeatherStyleId() {
		return PIPING_MATCHING_LEATHER_STYLE_ID;
	}

	static get stitchingMatchingLeatherStyleId() {
		return STITCHING_MATCHING_LEATHER_STYLE_ID;
	}

	static get embroideryOptionMatchingLeatherStyleId() {
		return EMBROIDERY_OPTION_MATCHING_LEATHER_STYLE_ID;
	}

	static get discontinuedLeatherStyleIds() {
		return DISCONTINUED_LEATHER_STYLE_IDS;
	}

	static get defaultLeatherStyleId() {
		return DEFAULT_LEATHER_STYLE_ID;
	}

	static get dashboardMatchingGlossBodyColourId() {
		return DASHBOARD_MATCHING_GLOSS_BODY_COLOUR_ID;
	}

	static get dashboardMatchingMatteBodyColourId() {
		return DASHBOARD_MATCHING_MATTE_BODY_COLOUR_ID;
	}

	static get seatPleatingOptionForTwoToneLeather() {
		return SEAT_PLEATING_OPTION_FOR_TWO_TONE_LEATHER;
	}

	static get silverSportsExhaustOption() {
		return SILVER_SPORTS_EXHAUST_OPTION;
	}

	static get blackSportsExhaustOption() {
		return BLACK_SPORTS_EXHAUST_OPTION;
	}

	static get cupHolderOption() {
		return CUP_HOLDER_OPTION;
	}

	static get connectivityPackOption() {
		return CONNECTIVITY_PACK_OPTION;
	}

	static get airConOption() {
		return AIR_CON_OPTION;
	}

	static get tunnelStorageOption() {
		return TUNNEL_STORAGE_OPTION;
	}

	static get sidescreenStorageOption() {
		return SIDESCREEN_STORAGE_OPTION;
	}

	static get preEquipmentPackOption() {
		return PRE_EQUIPMENT_PACK_OPTION;
	}

	static get blackLuggageRackOption() {
		return BLACK_LUGGAGE_RACK_OPTION;
	}

	static get heatedSeatsOption() {
		return HEATED_SEATS_OPTION;
	}

	static get comfortPlusOption() {
		return COMFORT_PLUS_OPTION;
	}

	static get polishedGrilleMeshOption() {
		return DEFAULT_GRILLE_MESH_OPTION_ID;
	}

	static get blackGrilleMeshOption() {
		return BLACK_GRILLE_MESH_OPTION_ID;
	}

	static get darkSatinSplitterOption() {
		return DEFAULT_SPLITTER_OPTION_ID;
	}

	static get silverSteeringWheelOption() {
		return DEFAULT_STEERING_WHEEL_OPTION_ID;
	}

	static get manualTransmissionOption() {
		return DEFAULT_TRANSMISSION_ID;
	}

	static get debugUsePdfV2Template() {
		return DEBUG_USE_PDFV2_TEMPLATE;
	}

	static get sendToDealerLeadId() {
		return this.platformIsMobile ? SEND_TO_DEALER_MOBILE_LEAD_ID : SEND_TO_DEALER_DESKTOP_LEAD_ID;
	}

	static get pdfDownloadLeadId() {
		return this.platformIsMobile ? PDF_DOWNLOAD_MOBILE_LEAD_ID : PDF_DOWNLOAD_DESKTOP_LEAD_ID;
	}

	//-----------------------------------------------------------------------------------------
	// Public Static Fields: (equivalent to read/write Properties in this context)
	//-----------------------------------------------------------------------------------------

	// ensure debug options are turned off before going live!!
	static debugOptions: DebugOptions = {
		autoStart: false,
		autoStartView: ViewStates.Accessories
	};

	static platformIsMobile = false; // set by scene controller on start.

	static modelId = MODEL_ID;
	static regionCode = DEFAULT_REGION_CODE;
	static languageCode = DEFAULT_LANGUAGE_CODE;

	static transmission = DEFAULT_TRANSMISSION_ID;
	static bodyPaintColour = DEFAULT_PAINT_COLOUR_ID;

	// the rgba of a bespoke body colour, held alongside the bespoke option id in 'bodyPaintColour' - the id carries the description
	// and price, this carries the colour itself. null whenever the selected paint is a standard one.
	static bespokeBodyPaintColour: NumberArray | null = null;

	// the same for the wings, which can be given a bespoke colour of their own. the two regions record the same option ids, so the
	// colour is the only thing that tells one region's bespoke paint from the other's - which is why isSamePaint() compares both.
	static bespokeWingPaintColour: NumberArray | null = null;

	// whether the special paint information has been shown on the customer's first selection of a bespoke colour. it is held here
	// because the paintwork view is unmounted on every change of step, and it is deliberately not cleared by a reset - the customer
	// has read it once already, so it is not shown again for the rest of the session.
	static hasShownSpecialPaintInformation = false;

	static wingPaintColour = DEFAULT_PAINT_COLOUR_ID;
	static wheelStyle = DEFAULT_WHEEL_STYLE_ID;
	static wheelNutOption = DEFAULT_WHEEL_NUT_OPTION_ID;
	static leatherStyle = DEFAULT_LEATHER_STYLE_ID;
	static tunnelSidesStyle = DEFAULT_TUNNEL_SIDES_STYLE_ID;
	static seatBackStyle = DEFAULT_SEAT_BACK_STYLE_ID;
	static leatherPipingOption = DEFAULT_LEATHER_PIPING_OPTION_ID;
	static seatStitchingOption = DEFAULT_SEAT_STITCHING_OPTION_ID;
	static carpetColour = DEFAULT_CARPET_COLOUR_ID;
	static dashboardOption = DEFAULT_DASHBOARD_OPTION_ID;
	static spareWheelOption = DEFAULT_SPARE_WHEEL_OPTION_ID;
	static steeringWheelOption = DEFAULT_STEERING_WHEEL_OPTION_ID;
	static hoodStyleOption = DEFAULT_HOOD_STYLE_OPTION_ID;
	static wingBeadingOption = DEFAULT_WING_BEADING_OPTION_ID;
	static seatPleatingOption = DEFAULT_SEAT_PLEATING_OPTION_ID;
	static headrestEmbroideryOption = DEFAULT_HEADREST_EMBROIDERY_OPTION_ID;
	static centreConsoleOption = DEFAULT_CENTRE_CONSOLE_OPTION_ID;
	static stoneGuardOption = DEFAULT_STONE_GUARD_OPTION_ID;
	static grilleMeshOption = DEFAULT_GRILLE_MESH_OPTION_ID;
	static splitterOption = DEFAULT_SPLITTER_OPTION_ID;
	static accentOption = DEFAULT_ACCENT_OPTION_ID;
	static accentPackOption = DEFAULT_ACCENT_PACK_OPTION_ID;
	static doorGraphicOption = DEFAULT_DOOR_GRAPHIC_OPTION_ID;
	static pilotValue = DEFAULT_PILOT_VALUE;
	static copilotValue = DEFAULT_COPILOT_VALUE;
	static customFlagValue = DEFAULT_CUSTOM_FLAG_VALUE;
	static callSignsFlagOption = DEFAULT_CALL_SIGNS_FLAG_OPTION_ID;
	static audioOption = DEFAULT_AUDIO_OPTION_ID;
	static paintProtectionFilmOption = DEFAULT_PAINT_PROTECTION_FILM_OPTION;

	static accessoryStorageOptions = new Set<string>();
	static accessoryCoversOptions = new Set<string>();
	static accessoryEquipmentOptions = new Set<string>();
	static accessoryServicesOptions = new Set<string>();
	static accessoryAftermarketOptions = new Set<string>();

	static lastBodyPaintColour = Configuration.bodyPaintColour;

	//-----------------------------------------------------------------------------------------
	// Public Functions:
	//-----------------------------------------------------------------------------------------

	static resetConfiguration() {
		this.transmission = DEFAULT_TRANSMISSION_ID;
		this.bodyPaintColour = DEFAULT_PAINT_COLOUR_ID;
		this.bespokeBodyPaintColour = null;
		this.wingPaintColour = DEFAULT_PAINT_COLOUR_ID;
		this.bespokeWingPaintColour = null;
		this.wheelStyle = DEFAULT_WHEEL_STYLE_ID;
		this.wheelNutOption = DEFAULT_WHEEL_NUT_OPTION_ID;
		this.spareWheelOption = DEFAULT_SPARE_WHEEL_OPTION_ID;
		this.leatherStyle = DEFAULT_LEATHER_STYLE_ID;
		this.tunnelSidesStyle = DEFAULT_TUNNEL_SIDES_STYLE_ID;
		this.seatBackStyle = DEFAULT_SEAT_BACK_STYLE_ID;
		this.leatherPipingOption = DEFAULT_LEATHER_PIPING_OPTION_ID;
		this.seatStitchingOption = DEFAULT_SEAT_STITCHING_OPTION_ID;
		this.carpetColour = DEFAULT_CARPET_COLOUR_ID;
		this.dashboardOption = DEFAULT_DASHBOARD_OPTION_ID;
		this.steeringWheelOption = DEFAULT_STEERING_WHEEL_OPTION_ID;
		this.hoodStyleOption = DEFAULT_HOOD_STYLE_OPTION_ID;
		this.wingBeadingOption = DEFAULT_WING_BEADING_OPTION_ID;
		this.seatPleatingOption = DEFAULT_SEAT_PLEATING_OPTION_ID;
		this.headrestEmbroideryOption = DEFAULT_HEADREST_EMBROIDERY_OPTION_ID;
		this.centreConsoleOption = DEFAULT_CENTRE_CONSOLE_OPTION_ID;
		this.stoneGuardOption = DEFAULT_STONE_GUARD_OPTION_ID;
		this.grilleMeshOption = DEFAULT_GRILLE_MESH_OPTION_ID;
		this.splitterOption = DEFAULT_SPLITTER_OPTION_ID;
		this.accentOption = DEFAULT_ACCENT_OPTION_ID;
		this.accentPackOption = DEFAULT_ACCENT_PACK_OPTION_ID;
		this.doorGraphicOption = DEFAULT_DOOR_GRAPHIC_OPTION_ID;
		this.pilotValue = DEFAULT_PILOT_VALUE;
		this.copilotValue = DEFAULT_COPILOT_VALUE;
		this.customFlagValue = DEFAULT_CUSTOM_FLAG_VALUE;
		this.callSignsFlagOption = DEFAULT_CALL_SIGNS_FLAG_OPTION_ID;
		this.audioOption = DEFAULT_AUDIO_OPTION_ID;
		this.paintProtectionFilmOption = DEFAULT_PAINT_PROTECTION_FILM_OPTION;

		this.accessoryStorageOptions = new Set<string>();
		this.accessoryCoversOptions = new Set<string>();
		this.accessoryEquipmentOptions = new Set<string>();
		this.accessoryServicesOptions = new Set<string>();
		this.accessoryAftermarketOptions = new Set<string>();

		this.updateCosts();
	}


	// toggles the given accessory option, adding or removing the given ID from the list, and updating the costs.
	static toggleAccessoryOption(accessoryOptions: Set<string>, id: string) {
		if (accessoryOptions.has(id)) {
			accessoryOptions.delete(id);
		}
		else {
			accessoryOptions.add(id);
		}
		this.updateCosts();
	}

	// adds the given ID to the given accessory options, updating the costs.
	static addAccessoryOption(accessoryOptions: Set<string>, id: string) {
		accessoryOptions.add(id);
		this.updateCosts();
	}

	// removes the given ID from the given accessory options, updating the costs.
	static removeAccessoryOption(accessoryOptions: Set<string>, id: string) {
		if (accessoryOptions.has(id)) {
			accessoryOptions.delete(id);
		}
		this.updateCosts();
	}

	// updates the cost fields (cost breakdown, total cost and invalid option selected flag).
	static updateCosts() {

		// this will be set to true by the "getPrice" function calls if any of the prices returned are invalid.
		const isInvalidOptionRef = {
			ref: false
		}

		// transmission - n.b. this actually determines the base price of the vehicle.
		const transmissionCost = CloudData.getPrice(this.transmission, CloudData.transmissionOptions, isInvalidOptionRef);
		this._costBreakdown.transmission = transmissionCost;

		// paintwork.
		const bodyPaintCost = CloudData.getPrice(this.bodyPaintColour, CloudData.paintworkColourOptions, isInvalidOptionRef);
		const twoTonePaintCost = CloudData.getPrice(this.isTwoTonePaint() ? PaintColourWaysOptions.TwoTone : PaintColourWaysOptions.SingleColour, CloudData.paintworkColourWaysOptions, isInvalidOptionRef);
		const wingPaintCost = this.isTwoTonePaint() ? CloudData.getPrice(this.wingPaintColour, CloudData.paintworkColourOptions, isInvalidOptionRef) : 0;
		this._costBreakdown.paintwork = bodyPaintCost + twoTonePaintCost + wingPaintCost;

		// wheels.
		// n.b the cost when the spare wheel is included is for all five wheels, so it's one or the other.
		const wheelStyle = this.spareWheelOption === SpareWheelOptions.Spare ? CloudData.getSpareWheelId(this.wheelStyle) : this.wheelStyle;

		const wheelStyleCost = CloudData.getPrice(wheelStyle, CloudData.wheelStyleOptions, isInvalidOptionRef);
		const wheelNutOptionCost = CloudData.getPrice(this.wheelNutOption, CloudData.wheelNutOptions, isInvalidOptionRef);
		this._costBreakdown.wheels = wheelStyleCost + wheelNutOptionCost;

		// interior.
		const leatherStyleCost = CloudData.getPrice(this.leatherStyle, CloudData.leatherStyleOptions, isInvalidOptionRef);
		const tunnelSideStyleCost = CloudData.getPrice(this.tunnelSidesStyle, CloudData.leatherStyleOptions, isInvalidOptionRef);
		const seatPleatingOptionCost = CloudData.getPrice(this.seatPleatingOption, CloudData.seatPleatingOptions, isInvalidOptionRef);
		const seatBackStyleCost = CloudData.getPrice(this.seatBackStyle, CloudData.seatBackStyleOptions, isInvalidOptionRef);
		const headsetEmbroideryCost = CloudData.getPrice(this.headrestEmbroideryOption, CloudData.headrestEmbroideryOptions, isInvalidOptionRef);
		const leatherPipingCost = CloudData.getPrice(this.leatherPipingOption, CloudData.leatherPipingOptions, isInvalidOptionRef);
		const seatStitchingCost = CloudData.getPrice(this.seatStitchingOption, CloudData.seatStitchingOptions, isInvalidOptionRef);
		const carpetColorCost = CloudData.getPrice(this.carpetColour, CloudData.carpetColourOptions, isInvalidOptionRef);
		this._costBreakdown.interior = leatherStyleCost + tunnelSideStyleCost + seatPleatingOptionCost + seatBackStyleCost + headsetEmbroideryCost + leatherPipingCost + seatStitchingCost + carpetColorCost;

		// cockpit:
		// n.b. the 'finish' options are additional cost items from the relevant options table.
		const dashboardOptionCost = CloudData.getPrice(this.dashboardOption, CloudData.dashboardOptions, isInvalidOptionRef);
		const steeringWheelOptionCost = CloudData.getPrice(this.steeringWheelOption, CloudData.steeringWheelOptions, isInvalidOptionRef);
		const centreConsoleOptionCost = CloudData.getPrice(this.centreConsoleOption, CloudData.centreConsoleOptions, isInvalidOptionRef);
		this._costBreakdown.cockpit = dashboardOptionCost + steeringWheelOptionCost + centreConsoleOptionCost;

		// exterior:
		const hoodStyleOptionCost = CloudData.getPrice(this.hoodStyleOption, CloudData.hoodStyleOptions, isInvalidOptionRef);
		const wingBeadingOptionCost = CloudData.getPrice(this.wingBeadingOption, CloudData.wingBeadingOptions, isInvalidOptionRef);
		const stoneGuardOptionCost = CloudData.getPrice(this.stoneGuardOption, CloudData.stoneGuardOptions, isInvalidOptionRef);
		const grilleMeshOptionCost = CloudData.getPrice(this.grilleMeshOption, CloudData.grilleMeshOptions, isInvalidOptionRef);
		const doorGraphicOptionCost = CloudData.getPrice(this.doorGraphicOption, CloudData.graphicsOptions, isInvalidOptionRef);
		const callSignsGraphicOptionCost = CloudData.getPrice(this.callSignsFlagOption, CloudData.graphicsOptions, isInvalidOptionRef);
		const splitterOptionCost = CloudData.getPrice(this.splitterOption, CloudData.accessoryOptions, isInvalidOptionRef);
		const accentOptionCost = CloudData.getPrice(this.accentOption, CloudData.badgeOptions, isInvalidOptionRef);
		const accentPackOptionCost = CloudData.getPrice(this.accentPackOption, CloudData.accessoryOptions, isInvalidOptionRef);
		const paintProtectionFilmOption = CloudData.getPrice(this.paintProtectionFilmOption, CloudData.accessoryOptions, isInvalidOptionRef);
		this._costBreakdown.exterior =
			hoodStyleOptionCost + wingBeadingOptionCost + stoneGuardOptionCost + grilleMeshOptionCost +
			doorGraphicOptionCost + callSignsGraphicOptionCost + splitterOptionCost +
			accentOptionCost + accentPackOptionCost + paintProtectionFilmOption;
		
		// accessories:
		const accessoryStorageOptionsCost = CloudData.getAccessoryPrices(this.accessoryStorageOptions, isInvalidOptionRef);
		const accessoryCoversOptionsCost = CloudData.getAccessoryPrices(this.accessoryCoversOptions, isInvalidOptionRef);
		const accessoryEquipmentOptionsCost = CloudData.getAccessoryPrices(this.accessoryEquipmentOptions, isInvalidOptionRef);
		const accessoryServicesOptionsCost = CloudData.getAccessoryPrices(this.accessoryServicesOptions, isInvalidOptionRef);
		const accessoryAftermarketOptionsCost = CloudData.getAccessoryPrices(this.accessoryAftermarketOptions, isInvalidOptionRef);
		const accessoryAudioOptionCost = CloudData.getPrice(this.audioOption, CloudData.accessoryOptions, isInvalidOptionRef);
		this._costBreakdown.accessories =
			accessoryStorageOptionsCost + accessoryCoversOptionsCost + accessoryEquipmentOptionsCost +
			accessoryServicesOptionsCost + accessoryAftermarketOptionsCost + accessoryAudioOptionCost;

		// update total cost from the breakdown.
		this._totalCost =
			this._costBreakdown.transmission +
			this._costBreakdown.paintwork +
			this._costBreakdown.wheels +
			this._costBreakdown.interior +
			this._costBreakdown.cockpit +
			this._costBreakdown.exterior +
			this._costBreakdown.accessories;

		// update the flag.
		this._invalidOptionSelected = isInvalidOptionRef.ref;
	}

	static isBespokePaintColour(id: string): boolean {
		return (Object.values(BespokePaintColours) as string[]).indexOf(id) !== -1;
	}

	/**
	 * Returns whether two paint selections are the same paint. A selection is an option id and, for a bespoke id, the colour chosen
	 * for it: a bespoke option id stands for 'a special colour of this finish' rather than for one particular colour, and the same
	 * ids serve the body and the wings, so two bespoke selections are only the same paint when their colours match too.
	 */
	static isSamePaint(firstId: string, firstColour: NumberArray | null, secondId: string, secondColour: NumberArray | null): boolean {

		// different options are always different paints.
		if (firstId !== secondId) {
			return false;
		}

		// a standard option id identifies its colour on its own, so matching ids are enough.
		if (!this.isBespokePaintColour(firstId)) {
			return true;
		}

		// a bespoke id does not, so the chosen colours decide it. a colour that has not been chosen matches only another that has not.
		if (!firstColour || !secondColour) {
			return firstColour === secondColour;
		}
		return firstColour[0] === secondColour[0] && firstColour[1] === secondColour[1] && firstColour[2] === secondColour[2];
	}

	/**
	 * Returns whether the wings are wearing whatever the body was last painted - the test for 'the wings have been following the body
	 * colour', which is what decides whether they follow it again when the body colour changes.
	 * N.B. must be read before the new body colour is recorded, as it compares against the body colour currently held.
	 */
	static isWingPaintFollowingBodyPaint(): boolean {
		return this.isSamePaint(this.lastBodyPaintColour, this.bespokeBodyPaintColour, this.wingPaintColour, this.bespokeWingPaintColour);
	}

	static isTwoTonePaint(): boolean {
		return !this.isSamePaint(this.bodyPaintColour, this.bespokeBodyPaintColour, this.wingPaintColour, this.bespokeWingPaintColour);
	}

	static isUsRegion(): boolean {
		return this.regionCode === 'us';
	}
}
